import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { PageHero } from '@/components/sections/page-hero'
import { Cta } from '@/components/sections/cta'

const sections = [
    {
        title: 'Who we are',
        body: 'Funnel is a paid advertising agency operating from 18 Hanbury Street, London E1 6QR. If you have a question about anything on this page, email hello@funnel.agency and a human will answer.'
    },
    {
        title: 'What we collect',
        body: 'We collect only what you give us: the name, email address, company and message you submit through our contact form, and the email address you give us if you join our newsletter. We also collect anonymous, aggregate analytics about how people use this site.'
    },
    {
        title: 'Why we collect it',
        body: 'Contact details are used to reply to your enquiry and, if we start working together, to run your account. Newsletter addresses are used to send you our updates and nothing else. We do not sell your data, and we do not share it with third parties for their own marketing.'
    },
    {
        title: 'How long we keep it',
        body: 'Enquiries are kept for two years so we can pick up a conversation where it left off. Newsletter subscriptions are kept until you unsubscribe, which you can do from the footer of any email we send.'
    },
    {
        title: 'Cookies',
        body: 'This site uses only the cookies it needs to function and to measure traffic in aggregate. We do not use advertising cookies to track you across other websites from this site.'
    },
    {
        title: 'Your rights',
        body: 'You can ask us for a copy of the data we hold about you, ask us to correct it, or ask us to delete it. Email hello@funnel.agency and we will action the request within 30 days.'
    },
    {
        title: 'Terms of engagement',
        body: 'Campaign work is delivered under a separate written agreement covering scope, fees, notice periods and ownership of ad accounts and creative. Ad accounts and creative assets produced for you remain yours. Nothing on this website forms part of that agreement or constitutes a binding offer.'
    },
    {
        title: 'Results and claims',
        body: 'Figures shown on this site reflect results achieved for specific clients in specific conditions. Advertising performance varies with market, offer, margin and budget, and past results are not a guarantee of future performance.'
    }
]

const Legal: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Legal'
                description='Privacy, cookies, data handling and terms of engagement for Funnel.'
            />

            <PageHero
                showLogos={false}
                segments={[
                    { text: 'The' },
                    { text: 'small print,', serif: true },
                    { text: 'in plain English.' }
                ]}
                subtitle='How we handle your data, what we do with it, and the terms we work under. Last updated September 2026.'
            />

            <Section
                className='pt-0 pb-[100px] lg:pt-0 lg:pb-[120px]'
                containerClassName='max-w-[760px] items-stretch'
            >
                <Reveal
                    y={22}
                    blur={10}
                    stagger={0.06}
                    className='flex flex-col gap-10'
                >
                    {sections.map((section) => (
                        <div
                            key={section.title}
                            className='flex flex-col gap-3'
                        >
                            <h2 className='text-[clamp(1.25rem,2vw,1.5rem)] leading-tight font-medium tracking-[-0.03em] text-white'>
                                {section.title}
                            </h2>

                            <p className='text-[17px] leading-[1.7] tracking-[-0.01em] text-white/56'>
                                {section.body}
                            </p>
                        </div>
                    ))}
                </Reveal>
            </Section>

            <Cta />
        </Layout>
    )
}

export default Legal
